import { displayValue } from '../../lib/oem-display';
import { RecordGallery } from './record-gallery';
import { runtimeMode } from '../../lib/auth';
import { ArrowRight, ChevronRight, Clock } from 'lucide-react';
import type { OemWorkspace } from '../../lib/oem-client';
import { productionQuantity } from '@sqi/formula-core';
export function ProductPackaging({
  workspace,
  onRequest,
  busy,
  canRequest,
}: {
  readonly workspace: OemWorkspace;
  readonly onRequest: (skuId: string) => Promise<void>;
  readonly busy: boolean;
  readonly canRequest: boolean;
}) {
  const assemblies = workspace.records.filter((r) => r.kind === 'packaging');
  const samples = workspace.records.filter((r) => r.kind === 'sample');
  const accepted = workspace.decisions.filter(
    (d) => d.action === 'agree_criteria' && d.disposition === 'accepted',
  );
  const latest = (kind: string, skuId: string) =>
    workspace.records
      .filter((r) => r.kind === kind && r.skuId === skuId)
      .sort(
        (a, b) => b.version - a.version || Date.parse(b.createdAt) - Date.parse(a.createdAt),
      )[0];
  const primary = workspace.skus[0] ? latest('brief', workspace.skus[0].id) : undefined;
  const count = runtimeMode === 'fixture' ? '5000' : displayValue(primary?.content.quantity ?? '');
  const fill =
    runtimeMode === 'fixture'
      ? '30'
      : primary?.content.fillUnit === 'mL'
        ? displayValue(primary?.content.fillSize ?? '')
        : '';
  let quantity: null | ReturnType<typeof productionQuantity> = null;
  try {
    if (count && fill) quantity = productionQuantity(count, fill);
  } catch {
    /* unknown or invalid quantity remains visibly unknown */
  }
  return (
    <>
      <div className="crumb">
        โครงการ <ChevronRight />
        {workspace.project.label}
        <ChevronRight />
        รายละเอียดฉบับที่เลือก
      </div>
      <div className="page-title">
        <div>
          <div className="eyebrow">PRODUCT &amp; PACKAGING</div>
          <h1>
            {runtimeMode === 'fixture' ? (
              <>
                ครีมหนึ่งสูตร
                <br />
                สองรูปแบบบรรจุภัณฑ์
              </>
            ) : (
              'ผลิตภัณฑ์และรูปแบบบรรจุภัณฑ์'
            )}
          </h1>
          <p className="muted wrap">
            {workspace.project.label} · {workspace.project.brand}
          </p>
        </div>
        <span className="chip amber">
          <Clock />
          ข้อกำหนดยังไม่ครบ
        </span>
      </div>
      <div className="grid2">
        <section>
          <RecordGallery
            key={workspace.project.id + assemblies.map((r) => r.id).join(',')}
            projectId={workspace.project.id}
            recordIds={assemblies.flatMap((r) => [
              r.id,
              ...(Array.isArray(r.content.componentIds)
                ? r.content.componentIds.filter((id): id is string => typeof id === 'string')
                : []),
            ])}
            label="บรรจุภัณฑ์ฉบับที่เลือก"
            className="photo-large"
          />
          <div className="sectionhead">
            <h2>โปรไฟล์เนื้อครีม</h2>
          </div>
          <div className="card">
            <div className="detail-row">
              <span className="muted">ความต้องการ</span>
              <span>
                {runtimeMode === 'fixture'
                  ? 'เกลี่ยง่าย · สัมผัสเบา'
                  : displayValue(primary?.content.summary ?? 'ยังไม่ทราบความต้องการ')}
              </span>
            </div>
            <div className="detail-row">
              <span className="muted">ผลที่สังเกต</span>
              <span>
                {samples.length
                  ? `${samples.length} ตัวอย่าง · ความเห็นแยกตามตัวอย่าง`
                  : 'ยังไม่มีตัวอย่าง'}
              </span>
            </div>
            <div className="detail-row">
              <span className="muted">เกณฑ์ที่ตกลง</span>
              <span>
                {accepted.length
                  ? accepted.map((d) => d.criteria.join(' / ')).join(' · ')
                  : 'ยังไม่มีการรับเกณฑ์'}
                <br />
                ดูขอบเขตและความคิดเห็นในรอบตัวอย่าง
              </span>
            </div>
          </div>
        </section>
        <section className="stack">
          {workspace.skus.map((sku, i) => {
            const assembly = assemblies
              .filter((r) => r.skuId === sku.id)
              .sort(
                (a, b) =>
                  b.version - a.version || Date.parse(b.createdAt) - Date.parse(a.createdAt),
              )[0];
            const brief = latest('brief', sku.id);
            const technicallyReviewed =
              assembly &&
              workspace.decisions
                .filter((d) => d.recordId === assembly.id && d.action === 'review_technical')
                .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0]
                ?.disposition === 'passed';
            return (
              <div className="card" key={sku.id}>
                <div className="eyebrow">
                  {sku.label} · {displayValue(assembly?.content.label ?? 'ยังไม่มี assembly')} v
                  {assembly?.version ?? '—'}
                </div>
                <h2>
                  {runtimeMode === 'fixture'
                    ? i === 0
                      ? 'Airless pump concept'
                      : 'Jar concept'
                    : displayValue(assembly?.content.component ?? 'รูปแบบยังไม่ทราบ')}
                </h2>
                <p className="muted">
                  {runtimeMode === 'fixture'
                    ? i === 0
                      ? '30 mL · 5,000 หน่วยที่ขอ'
                      : '50 mL · 2,000 หน่วยที่ขอ'
                    : `${displayValue(brief?.content.fillSize ?? 'ยังไม่ทราบ')} ${displayValue(brief?.content.fillUnit ?? '')} · ${displayValue(brief?.content.quantity ?? 'ยังไม่ทราบ')} หน่วยที่ขอ`}
                </p>
                <div className="detail-list">
                  <div className="detail-row">
                    <span>
                      {runtimeMode === 'fixture'
                        ? i === 0
                          ? 'ขวด / ปั๊ม / ฝา / ฉลาก'
                          : 'กระปุก / ฝา / ซีล / ฉลาก'
                        : 'ชุดบรรจุภัณฑ์ฉบับที่เลือก'}
                    </span>
                    <span>{displayValue(assembly?.content.component ?? 'รอสเปกผู้ขาย')}</span>
                  </div>
                  <div className="detail-row">
                    <span>วัสดุและขนาด</span>
                    <span>{displayValue(assembly?.content.dimensions ?? 'ยังไม่ทราบ')}</span>
                  </div>
                  <div className="detail-row">
                    <span>ความเข้ากันได้</span>
                    <span>
                      {displayValue(assembly?.content.compatibilitySource ?? 'รอ technical review')}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>อาร์ตเวิร์ก</span>
                    <span>แยกตามสินค้าและแบบฉบับที่เลือก</span>
                  </div>
                </div>
                <span className="chip amber">
                  {technicallyReviewed
                    ? 'มีผลทบทวนเทคนิคในเวอร์ชันนี้'
                    : 'ต้องประเมินความเข้ากันได้'}
                </span>
                <button
                  className="action secondary"
                  disabled={busy || !canRequest}
                  onClick={() => void onRequest(sku.id)}
                >
                  ขอรายละเอียดชิ้นส่วน <ArrowRight />
                </button>
              </div>
            );
          })}
          <div className="note-warning">
            <strong>
              {quantity
                ? `${fill} mL × ${count} = ${quantity.litres} L`
                : 'ยังคำนวณปริมาตรไม่ได้ · ขนาดหรือจำนวนยังไม่ทราบ'}
            </strong>
            <br />
            ต้องมีความหนาแน่นที่วัดและตรวจทานบนสูตรฉบับที่เลือกก่อนใช้เป็นกิโลกรัม ·
            ดูหลักฐานที่ได้รับสิทธิ์ในเวอร์ชันนี้ ภาพและ nominal capacity ไม่ยืนยันความเหมาะสม
          </div>
        </section>
      </div>
      <div className="footer-actions">
        <div>
          <strong>เลือกได้เมื่อสเปกและผลประเมินพร้อม</strong>
          <p className="mock-note">ภาพแนวคิด · ยังไม่ใช่บรรจุภัณฑ์ที่อนุมัติผลิต</p>
        </div>
        <a className="action secondary" href="./specifications">
          ดูสเปกและหลักฐาน <ArrowRight />
        </a>
      </div>
    </>
  );
}
